import React, { useMemo } from 'react';
import classNames from 'classnames';
import { find } from 'lodash-es';
import {
  ConfigProvider,
  Icon,
  StatusTip,
  Table,
} from 'tea-component';
import type { SortBy } from 'tea-component/lib/table/addons';
import { getLocale, useIntl } from 'umi';
import '@op/components/TableCommonHook/index.scss';
import { LANGUAGE_EN } from '@home/_config/config';
import BasicImage from '@home/components/BasicImage';
import { dateAllFormat } from '@home/utils/common';
import { getTeaLocale } from '@/pages/home/utils/lang';
import OpenChatIcon from '@/pages/aigc/assets/svg/icon-file-library-open-chat.svg';
import DownloadIcon from '@/pages/aigc/assets/svg/icon-file-library-download.svg';
import UserUploadIcon from '@/pages/aigc/assets/svg/icon-file-library-user-upload.svg';
import type { IAgentData } from '../../../AgentCollection/types';
import SkillDetailEmptyState from '../../../SkillsCollection/SkillDetail/components/SkillDetailEmptyState';
import type { ISkillData } from '../../../SkillsCollection/types';
import { FILE_LIBRARY_SORTABLE_COLUMNS } from '../../utils/query';
import SkillSourceCell from '../SkillSourceCell';
import EllipsisHover from '@/pages/aigc/components/EllipsisHover';
import type { TDatabrainFile } from '../../types';
import {
  formatFileSize,
  getAgentSourceDisplayName,
  getFileLibraryIcon,
  getFileSourceDisplay,
  getFileTypeLabel,
  getFileTypeModifier,
} from '../../utils';

const { sortable, autotip } = Table.addons;

/** 文件库表格（tea Table，外层 body 滚动） */
const FileLibraryTable: React.FC<{
  files: TDatabrainFile[];
  loading: boolean;
  loadingMore: boolean;
  sortInfo: SortBy<string>;
  authorizedAgents: IAgentData[];
  allSkills: ISkillData[];
  onSortChange: (sort?: SortBy<string>) => void;
  onOpenChat: (file: TDatabrainFile) => void;
  onDownload: (file: TDatabrainFile) => void;
}> = ({
  files,
  loading,
  loadingMore,
  sortInfo,
  authorizedAgents,
  allSkills,
  onSortChange,
  onOpenChat,
  onDownload,
}) => {
  const { locale, formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const teaLocale = getTeaLocale(getLocale());
  const isTableEmpty = files.length === 0;

  const columns = useMemo(
    () => [
      {
        key: 'file_name',
        header: formatMessage({ id: 'dba_file_library_col_name' }),
        render: (file: TDatabrainFile) => (
          <span className="dba-file-library__name">
            <img
              src={getFileLibraryIcon(file.file_type)}
              alt=""
              className="dba-file-library__file-icon"
            />
            <EllipsisHover text={file.file_name} />
          </span>
        ),
      },
      {
        key: 'source',
        header: formatMessage({ id: 'dba_file_library_col_source' }),
        render: (file: TDatabrainFile) => {
          const sourceDisplay = getFileSourceDisplay(file, isEN, allSkills);
          if (sourceDisplay?.kind === 'skill' && sourceDisplay.items.length > 0) {
            return <SkillSourceCell items={sourceDisplay.items} />;
          }
          const agent = find(
            authorizedAgents,
            item => item.agentId === file.model_id,
          );
          const name =
            sourceDisplay?.kind === 'agent'
              ? sourceDisplay.label
              : getAgentSourceDisplayName(agent, file.model_id, isEN);
          if (!file.model_id) {
            return (
              <span className="dba-file-library__source dba-file-library__source--user">
                <img src={UserUploadIcon} alt="" />
                <EllipsisHover text={name} />
              </span>
            );
          }
          return (
            <span className="dba-file-library__source dba-file-library__source--agent">
              {agent?.icon ? <BasicImage src={agent.icon} /> : null}
              <EllipsisHover text={name} />
            </span>
          );
        },
      },
      {
        key: 'created_at',
        header: formatMessage({ id: 'dba_file_library_col_created' }),
        render: (file: TDatabrainFile) =>
          dateAllFormat(file.created_at, 'hours'),
      },
      {
        key: 'file_size',
        header: formatMessage({ id: 'dba_file_library_col_size' }),
        align: 'right' as const,
        render: (file: TDatabrainFile) => formatFileSize(file.file_size),
      },
      {
        key: 'file_type',
        header: formatMessage({ id: 'dba_file_library_col_type' }),
        render: (file: TDatabrainFile) => {
          if (!file.file_type?.trim()) {
            return null;
          }
          return (
            <span
              className={`dba-file-library__type dba-file-library__type--${getFileTypeModifier(
                file.file_type,
              )}`}
            >
              {getFileTypeLabel(file.file_type)}
            </span>
          );
        },
      },
      {
        key: 'actions',
        header: formatMessage({ id: 'dba_file_library_col_actions' }),
        render: (file: TDatabrainFile) => (
          <span className="dba-file-library__actions">
            <button
              type="button"
              className="dba-file-library__action"
              onClick={() => onOpenChat(file)}
            >
              <img src={OpenChatIcon} alt="" />
              {formatMessage({ id: 'dba_file_library_open_chat' })}
            </button>
            <button
              type="button"
              className="dba-file-library__action"
              onClick={() => onDownload(file)}
            >
              <img src={DownloadIcon} alt="" />
              {formatMessage({ id: 'dba_file_library_download' })}
            </button>
          </span>
        ),
      },
    ],
    [allSkills, authorizedAgents, formatMessage, isEN, onDownload, onOpenChat],
  );

  const tableAddons = [
    sortable({
      columns: FILE_LIBRARY_SORTABLE_COLUMNS.map(key => ({
        prefer: 'desc',
        key,
      })),
      value: [sortInfo],
      onChange: value => {
        onSortChange(value?.[0]);
      },
    }),
    autotip({
      isLoading: loading && files.length === 0,
      hideIcon: true,
      loadingText: (
        <div className="dba-file-library__table-state dba-file-library__table-state--loading">
          <Icon type="loading" style={{ marginRight: 5 }} />
          {formatMessage({ id: 'dba_file_library_loading' })}
        </div>
      ),
      emptyText: (
        <div className="dba-file-library__table-state">
          <SkillDetailEmptyState
            message={formatMessage({ id: 'dba_file_library_empty' })}
          />
        </div>
      ),
    }),
  ];

  return (
    <>
      <ConfigProvider locale={teaLocale}>
        <Table
          verticalTop
          className={classNames('dbo-tablecommon dba-file-library__table', {
            'dba-file-library__table--empty': isTableEmpty || loading,
          })}
          records={files}
          columns={columns}
          addons={tableAddons}
        />
      </ConfigProvider>
      {loadingMore ? (
        <div className="dba-file-library__loading-more">
          <StatusTip
            status="loading"
            loadingText={formatMessage({ id: 'dba_file_library_loading' })}
          />
        </div>
      ) : null}
    </>
  );
};

export default FileLibraryTable;
